import { useNavigate, Link } from "react-router";
import { useClientContext } from "./ClientWorkspace";
import { flowLabel, isActive, type ClientPipeline } from "./client-model";
import { transformDescription, vendorLabel } from "../../lib/friendly-labels";
import { Table, type Column } from "../../components/primitives/Table";
import { Badge } from "../../components/primitives/Badge";
import { EmptyState } from "../../components/primitives/EmptyState";
import { ButtonLink } from "../../components/primitives/ButtonLink";

/**
 * Overview tab (spec §3.3): what this client has connected and what is
 * running. Read-only on purpose — editing lives on Connections and Settings,
 * so the landing tab cannot change anything by accident.
 */
export function ClientOverview() {
  const { org, connections, pipelines } = useClientContext();
  const nav = useNavigate();

  const columns: Column<ClientPipeline>[] = [
    {
      key: "name",
      label: "PIPELINE",
      render: (p) => (
        <div>
          <div className="mono">{p.name}</div>
          <div className="run-sub">{transformDescription(p.transformId)}</div>
        </div>
      ),
    },
    { key: "flow", label: "FLOW", render: (p) => flowLabel(p) },
    { key: "env", label: "ENVIRONMENT", render: (p) => <span className="mono">{p.environment ?? "—"}</span> },
    {
      key: "status",
      label: "STATUS",
      render: (p) => <Badge tone={isActive(p) ? "ok" : "muted"}>{isActive(p) ? "Active" : "Paused"}</Badge>,
    },
  ];

  return (
    <div>
      <section className="client-section">
        <div className="uppercase-label client-section__title">CONNECTIONS</div>
        {connections.length === 0 ? (
          <p className="page-help">No connections yet.</p>
        ) : (
          <ul className="client-connections">
            {connections.map((c) => (
              <li key={c.id} className="client-connections__row">
                <Link to={`/connections/${c.id}`}>{vendorLabel(c.connectorType)}</Link>
                <span className="mono client-connections__name">{c.name}</span>
                <Badge tone={c.environment == null ? "muted" : "ok"}>{c.environment ?? "Any environment"}</Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="client-section">
        <div className="uppercase-label client-section__title">PIPELINES</div>
        <Table
          columns={columns}
          rows={pipelines}
          rowKey={(p) => p.id}
          onRowClick={(p) => nav(`/pipelines/${p.id}`)}
          empty={
            <EmptyState
              title="No pipelines for this client yet."
              action={
                <ButtonLink to={`/pipelines/new?organizationId=${encodeURIComponent(org.id)}`}>
                  New pipeline
                </ButtonLink>
              }
            />
          }
        />
      </section>
    </div>
  );
}
